/**
 * The review queue's rules, in one place: what to fetch, what order to show it in, why, and how
 * to turn a stored permalink into a link that goes somewhere.
 *
 * These used to live at the top of `ReviewQueue.tsx` mixed in with the rendering. They are the
 * part of this screen that gets tuned (the order especially), so they belong somewhere that can
 * be read and changed without scrolling past JSX.
 */

import type { Draft, DraftState } from './types';

/** One round trip for everything a card shows. */
export const QUEUE_SELECT = [
  'id, body, edited_body, state, model, prompt_version, kb_refs, guardrail_flags,',
  'duplicate_similarity, cost_gbp, tokens_in, tokens_out, created_at, updated_at,',
  'rejected_reason,',
  'prospect:prospects (',
  '  id, intent_score, corridor, city_mentioned, budget_gbp, timeline, question_type,',
  '  classifier_reason, stage,',
  '  item:items (',
  '    id, platform, url, permalink, author_handle, title, body, posted_at,',
  '    source:sources ( id, identifier, display_name )',
  '  )',
  ')',
].join(' ');

/**
 * The queue's three views.
 *
 * This was two - "Waiting" and "Already decided" - and the second one held two different things:
 * drafts that were really posted (`sent`, which wrote a delivery row and counted against the
 * day's cap) and drafts taken out by hand (`skipped`, `rejected`), which posted nothing at all.
 * Read together, the list answered neither "what went out" nor "what did I throw away".
 */
export type QueueView = 'pending' | 'posted' | 'rejected';

/** Nothing has been decided about these yet: what the queue acts on. */
export const OPEN_STATES: DraftState[] = ['pending', 'approved'];

/** Posted for real. A delivery row exists, so the caps, the gaps and the survival check read it. */
export const POSTED_STATES: DraftState[] = ['sent'];

/** Taken out by hand, nothing posted. This is where "Put back in the queue" lives. */
export const REJECTED_STATES: DraftState[] = ['skipped', 'rejected'];

/**
 * Which states each view fetches.
 *
 * `approved` is in `pending` only because rows predating the removed Approve step may still carry
 * it. `failed` is in none of them: the schema allows it, nothing writes it.
 */
export const VIEW_STATES: Record<QueueView, DraftState[]> = {
  pending: OPEN_STATES,
  posted: POSTED_STATES,
  rejected: REJECTED_STATES,
};

/**
 * Newest *created* first is right for work not yet done. For the two decided views the last change
 * is the interesting one, because that is what moved the row there.
 */
export const VIEW_ORDER: Record<QueueView, 'created_at' | 'updated_at'> = {
  pending: 'created_at',
  posted: 'updated_at',
  rejected: 'updated_at',
};

/** The toggle's labels, and the order it shows them in. */
export const VIEW_LABELS: Record<QueueView, string> = {
  pending: 'Pending',
  posted: 'Posted',
  rejected: 'Rejected',
};

export const VIEWS: QueueView[] = ['pending', 'posted', 'rejected'];

/**
 * How far back the queue looks.
 *
 * Applied **server-side**, on `drafts.created_at`, and that is the whole point of it being a
 * query rather than a filter over `rows`. The list is capped at PAGE_SIZE, so a client-side
 * date filter would answer "the last 24 hours" with "the 50 newest rows, all of which happen
 * to be recent" - the same class of lie as counting fetched rows for the toggle badges.
 *
 * The date is when the *draft* was created, not when the stranger posted, so this reads as
 * "what has arrived since…" rather than "what was written since…". Those are different
 * questions and only the first one is about the queue.
 */
export type QueueWindow = 'any' | `${WindowDays}d`;

/** The windows offered, in days. One list, so the options and the maths cannot disagree. */
const WINDOW_DAYS = [1, 2, 3, 4, 5, 6, 7, 14] as const;

type WindowDays = (typeof WINDOW_DAYS)[number];

export const QUEUE_WINDOWS: Array<{ value: QueueWindow; label: string }> = [
  { value: 'any', label: 'Any time' },
  ...WINDOW_DAYS.map((days) => ({
    value: `${days}d` as QueueWindow,
    label: days === 1 ? 'Last 1 day' : `Last ${days} days`,
  })),
];

/** The instant a window starts at, or null for "any time". `now` is injectable so this is testable. */
export function windowSince(window: QueueWindow, now: number = Date.now()): string | null {
  if (window === 'any') return null;
  const days = Number(window.slice(0, -1));
  return new Date(now - days * 86_400_000).toISOString();
}

/**
 * How many days a window covers, for the timeline chart.
 *
 * `any` has no start, so it falls back to a fortnight rather than trying to draw every day since the
 * first draft. Everything else is the number in the key itself - `1d` is one bar, `14d` is fourteen -
 * so the chart and the list are always showing the same stretch of time.
 */
export const FALLBACK_WINDOW_DAYS = 14;

export function windowDays(window: QueueWindow): number {
  return window === 'any' ? FALLBACK_WINDOW_DAYS : Number(window.slice(0, -1));
}

export function isOpen(state: DraftState): boolean {
  return OPEN_STATES.includes(state);
}

/* ---------------------------------------------------------------------------
   Ordering
   ------------------------------------------------------------------------- */

const TIMELINE_RANK: Record<string, number> = {
  imminent: 5,
  '0-3_months': 4,
  '3-12_months': 2,
  '12_months_plus': 1,
  unspecified: 0,
};

/** The question types that are a destination decision rather than a detail. */
const DESTINATION_QUESTIONS = new Set(['where_to_live', 'housing', 'cost_of_living', 'schools']);

/**
 * Priority, in plan.md section 1.1's own order:
 *   1. a date - `imminent` before `0-3_months`, and both before anything vaguer;
 *   2. a destination decision still open - a named city, or a where-to-live / housing /
 *      cost-of-living / schools question, which is the decision NestMatch exists for;
 *   3. commitment signals, which is what a stated budget is;
 *   4. intent_score, then the US corridor as the final tie-breaker.
 *
 * Deliberately client-side: every input is already on `prospects`, so this costs no schema change
 * and can be re-tuned in a minute - which matters, because the first real week of drafts is what
 * will show whether this order is right.
 */
function priorityOf(row: Draft): number {
  const prospect = row.prospect;
  if (!prospect) return 0;

  let score = TIMELINE_RANK[prospect.timeline ?? 'unspecified'] ?? 0;
  if (prospect.city_mentioned) score += 3;
  if (DESTINATION_QUESTIONS.has(prospect.question_type ?? '')) score += 3;
  if (prospect.budget_gbp) score += 1;
  return score;
}

export function sortByIntent(rows: Draft[]): Draft[] {
  return [...rows].sort((a, b) => {
    const byPriority = priorityOf(b) - priorityOf(a);
    if (byPriority !== 0) return byPriority;

    const byIntent = (b.prospect?.intent_score ?? 0) - (a.prospect?.intent_score ?? 0);
    if (byIntent !== 0) return byIntent;

    // US corridor last, and only as a tie-breaker: plan.md treats it as a ranking input for two
    // otherwise equal prospects, never as a reason to prefer one over a better one.
    const byCorridor =
      Number(b.prospect?.corridor === 'us_to_uk') - Number(a.prospect?.corridor === 'us_to_uk');
    if (byCorridor !== 0) return byCorridor;

    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * Why this draft is where it is.
 *
 * The queue has always been sorted by a deliberate opinion and has never said so, which makes the
 * order look arbitrary to anyone who has not read this file. These are the same three inputs the
 * ranking uses, in the same order of importance, said out loud.
 */
export function priorityReasons(row: Draft): string[] {
  const prospect = row.prospect;
  if (!prospect) return [];

  const reasons: string[] = [];
  if (prospect.timeline === 'imminent') reasons.push('moving now');
  else if (prospect.timeline === '0-3_months') reasons.push('in 0–3 months');

  if (prospect.city_mentioned) reasons.push(`looking at ${prospect.city_mentioned}`);
  else if (DESTINATION_QUESTIONS.has(prospect.question_type ?? '')) reasons.push('destination open');

  if (prospect.budget_gbp) reasons.push('budget stated');
  return reasons;
}

/**
 * The deep link to the thread, made absolute.
 *
 * Reddit hands Apify permalinks as paths (`/r/IWantOut/comments/abc/why/`), and rows ingested
 * before 17 Sep 2026 were stored that way. A relative `href` resolves against the dashboard's own
 * origin, so "Open & reply" opened `/r/IWantOut/...` on the dashboard host and went nowhere.
 */
const SITE_ORIGIN: Record<string, string> = {
  reddit: 'https://www.reddit.com',
  facebook: 'https://www.facebook.com',
  instagram: 'https://www.instagram.com',
};

export function absoluteThreadUrl(
  platform: string | undefined,
  value: string | null | undefined,
): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const origin = SITE_ORIGIN[platform ?? ''];
  if (!origin) return '';
  return `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

/**
 * Where a duplicate shows up.
 *
 * `duplicate_similarity` has been fetched on every queue load since Phase 1 and never shown, which
 * makes the duplicate guard invisible in exactly the place it is supposed to be used. 0.82 is a
 * reporting threshold, not a block - the guard itself is P2.9's job, not the reviewer's.
 */
export const DUPLICATE_ALERT = 0.82;
